import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnInit,
  OnDestroy,
  OnChanges,
  SimpleChanges,
  inject,
  signal,
  computed,
  Input,
  HostBinding,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { toSignal } from '@angular/core/rxjs-interop';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { catchError, debounceTime, filter, forkJoin, map, of, Subject, takeUntil } from 'rxjs';
import { MessageService } from 'primeng/api';
import { ToastModule } from 'primeng/toast';
import { OrderControllerService } from '@app/api/order-service/api/orderController.service';
import { OrderBaseDTO } from '@app/api/order-service/model/orderBaseDTO';
import { ReceiverInfoDTO } from '@app/api/order-service/model/receiverInfoDTO';
import { OrderTotalVO } from '@app/api/order-service/model/orderTotalVO';
import { ItemSearchDTO } from '@app/api/order-service/model/itemSearchDTO';
import { PageInfoOrderItemPageVO } from '@app/api/order-service/model/pageInfoOrderItemPageVO';
import {
  FormField,
  TableSection,
  SummarySection,
  SummaryItem,
  ActionButton,
  SectionButton,
} from '@app/shared/components/add-form/add-form.component';
import { OrderCreateLayoutComponent } from '@app/shared/components/order-create-layout/order-create-layout.component';
import { O2OOrderService } from '../services/o2o-order.service';
import {
  formatCurrencyVND,
  formatDateTime,
} from '@app/shared/utils/date-formatter.util';
import { BlobConverterUtil } from '@app/shared/utils/blob-converter.util';
import {
  getO2OCreateConfig,
  O2OCreateConfig,
  PackageSkuChild,
} from '../services/o2o.config';
import { isLazadaO2oOrder } from '@app/feature/center-business/channel/utils/lazada-integration.util';
import {
  ORDER_INTERNAL_PAYMENT_METHOD_LIST,
  ORDER_INTERNAL_PAYMENT_STATUS_LIST,
  ORDER_INTERNAL_PICKUP_METHOD_LIST,
} from '@app/shared/utils/constants.util';
import { ProvinceService } from '../../channel/service/province.service';
import { PermissionService } from '@app/core/services/permission.service';
import { OrderSseService } from '@app/core/services/order-sse.service';
import { AuthService } from '@app/core/services/auth.service';
import {
  OrderModificationApprovalService,
  OrderModificationRequest,
} from '@app/feature/center-business/order/services/order-modification-approval.service';
import { resolveOrderDetailTotalDiscount } from '../services/o2o-promotion-checkout.util';
import {
  formatTimeoutMinutes,
  getStockHoldCategoryLabelKey,
  isStockHoldTimeoutOrder,
} from '@app/shared/utils/stock-hold-timeout.util';
import { SearchOrderEsParam } from '@app/api/order-service/model/searchOrderEsParam';
import { isPrePackedPackageFulfillment } from '../../order/internal/internal.config';

export type OrderViewFormMode = 'o2o' | 'standard_sales';

@Component({
  selector: 'app-o2o-order-view',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    OrderCreateLayoutComponent,
    ToastModule,
  ],
  templateUrl: './o2o-order-view.component.html',
  styleUrl: './o2o-order-view.component.css',
  changeDetection: ChangeDetectionStrategy.Default,
})
export class O2OOrderViewComponent implements OnInit, OnDestroy, OnChanges {
  private route = inject(ActivatedRoute);
  router = inject(Router);
  private orderService = inject(OrderControllerService);
  private readonly o2oOrderService = inject(O2OOrderService);
  private cdr = inject(ChangeDetectorRef);
  private translateService = inject(TranslateService);
  private messageService = inject(MessageService);
  private provinceService = inject(ProvinceService);
  private readonly orderSseService = inject(OrderSseService);
  private readonly permissionService = inject(PermissionService);
  private readonly authService = inject(AuthService);
  private readonly modificationApprovalService = inject(
    OrderModificationApprovalService,
  );
  private sseConnected = false;
  private readonly permissionCodes = toSignal(
    this.permissionService.userPermissions$,
    {
      initialValue: this.permissionService.getCurrentPermissions(),
    },
  );
  private destroy$ = new Subject<void>();

  // Input parameters for reusability
  @Input() enableEditButton: boolean = true;
  @Input() titleKey: string = 'o2o.view.title';
  @Input() backRoute: string = '/o2o/internal';
  @Input() textOnlyMode: boolean = false;
  @Input() orderIdParamName: string = 'orderId';
  /** When set (e.g. by marketplace order view), overrides summary section title. */
  @Input() summarySectionTitleKey?: string;
  /** Override create/view form config (e.g. Sales Order create layout). */
  @Input() createConfigFactory?: () => O2OCreateConfig;
  /**
   * Form field / data mapping mode.
   * `standard_sales` aligns Chi tiết Đơn hàng bán with Tạo Đơn hàng bán.
   */
  @Input() formMode: OrderViewFormMode = 'o2o';
  /** Embedded in dialog: load by id, hide header/back, skip SSE. */
  @Input() embedMode = false;
  /** Order id when {@link embedMode} is true (e.g. cancel/audit dialog). */
  @Input() inputOrderId: number | null = null;
  /** Hide toast when nested inside a parent that already shows toasts. */
  @Input() hideToast = false;
  @Input() permissionPrefix = 'o2o_orders.o2o_orders';
  @Input() enableSse = true;
  /** Marketplace order detail shows list price and voucher stored separately from the paid amount. */
  @Input() showMarketplacePrices = false;

  @HostBinding('class.o2o-order-view-embed')
  get hostEmbedClass(): boolean {
    return this.embedMode;
  }

  // Component state
  loading = signal(false);
  orderData = signal<OrderTotalVO | null>(null);
  orderId = signal<number | null>(null);
  stockHoldInfo = signal<{
    stockHoldCategory?: string;
    stockHoldTimeoutMinutes?: number;
    stockHoldExpiryTime?: string;
    failureReasonDescription?: string;
  } | null>(null);

  // Form data for initialData input (like create component)
  formData: any = {};

  // Form configuration - reuse O2O create config in view mode
  formConfig = signal<O2OCreateConfig | null>(null);
  formFields: FormField[] = [];
  tableSections: TableSection[] = [];
  summarySection: SummarySection | null = null;
  trailingSections: SummarySection[] = [];
  headerActions: ActionButton[] = [];
  sectionButtons: SectionButton[] = [];

  // Permission flags - computed from PermissionService
  // Using specific submenu 'o2o_orders' for O2O orders permissions
  canRead = computed(() => {
    this.permissionCodes();
    return this.permissionService.canDoAction(`${this.permissionPrefix}.view`);
  });
  canCreate = computed(() => {
    this.permissionCodes();
    return this.permissionService.canDoAction(`${this.permissionPrefix}.create`);
  });
  canUpdate = computed(() => {
    this.permissionCodes();
    return this.permissionService.canDoAction(`${this.permissionPrefix}.edit`);
  });
  canDelete = computed(() => {
    this.permissionCodes();
    return this.permissionService.canDoAction(`${this.permissionPrefix}.delete`);
  });
  canApprove = computed(() => {
    this.permissionCodes();
    return this.permissionService.canDoAction(`${this.permissionPrefix}.approve`);
  });

  maxModificationApproveLevel = computed(() => {
    this.permissionCodes();
    const prefix = this.permissionPrefix;
    if (
      this.permissionService.canDoAction(`${prefix}.approve_mod_l3`)
    ) {
      return 3;
    }
    if (
      this.permissionService.canDoAction(`${prefix}.approve_mod_l2`)
    ) {
      return 2;
    }
    if (
      this.permissionService.canDoAction(`${prefix}.approve_mod_l1`)
    ) {
      return 1;
    }
    return 0;
  });

  canApprovePendingModification = computed(() => {
    const pending = this.pendingModification();
    const level = this.maxModificationApproveLevel();
    return (
      !!pending &&
      pending.status === 'PENDING' &&
      level > 0 &&
      level >= (pending.requiredApprovalLevel || 1)
    );
  });

  // Inline edit state
  isEditingReceiverInfo = signal(false);
  pendingModification = signal<OrderModificationRequest | null>(null);
  originalReceiverData: any = {};
  provinceOptions = signal<Array<{ label: string; value: number }>>([]);
  // Store original province and ward IDs for saving (they're used for store filtering)
  originalProvinceId: number | null = null;
  originalWardId: number | null = null;

  // Utility functions
  formatCurrency = formatCurrencyVND;
  formatDateTime = formatDateTime;

  // Format date only (dd/MM/yyyy) for view mode
  formatDateOnly(dateInput: Date | string | null | undefined): string {
    if (!dateInput) return '';
    const dateObj = dateInput instanceof Date ? dateInput : new Date(dateInput);
    if (Number.isNaN(dateObj.getTime())) return '';

    const day = String(dateObj.getDate()).padStart(2, '0');
    const month = String(dateObj.getMonth() + 1).padStart(2, '0');
    const year = dateObj.getFullYear();

    return `${day}/${month}/${year}`;
  }

  private getProvinceName(provinceId: number): string {
    const province = this.provinceOptions().find((p) => p.value === provinceId);
    return province ? province.label : '';
  }

  ngOnInit(): void {
    this.loading.set(true);

    if (this.embedMode) {
      this.tryLoadEmbeddedOrder();
      return;
    }

    if (this.enableSse) {
      this.setupSseSubscription();
    }
    this.subscribeRouteOrderId();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      !this.embedMode ||
      !changes['inputOrderId'] ||
      changes['inputOrderId'].firstChange
    ) {
      return;
    }
    this.tryLoadEmbeddedOrder();
  }

  private subscribeRouteOrderId(): void {
    const loadFromRoute = (params: Record<string, string | undefined>) => {
      const orderId = Number(params[this.orderIdParamName]);
      if (orderId) {
        this.orderId.set(orderId);
        this.loadOrderDetails(orderId);
      } else {
        this.loading.set(false);
      }
    };

    if (this.enableEditButton) {
      this.provinceService.getProvinceOptions().subscribe((provinces) => {
        this.provinceOptions.set(provinces);
        this.route.params
          .pipe(takeUntil(this.destroy$))
          .subscribe((params) => loadFromRoute(params));
      });
      return;
    }

    this.route.params
      .pipe(takeUntil(this.destroy$))
      .subscribe((params) => loadFromRoute(params));
  }

  private tryLoadEmbeddedOrder(): void {
    const id = Number(this.inputOrderId);
    if (Number.isFinite(id) && id > 0) {
      this.orderId.set(id);
      this.loadOrderDetails(id);
      return;
    }
    this.loading.set(false);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    if (this.sseConnected) {
      this.orderSseService.disconnect();
    }
  }

  private setupSseSubscription(): void {
    this.orderSseService.connect();
    this.sseConnected = true;
    this.orderSseService.orderEvents
      .pipe(
        filter(
          (event) =>
            event.eventType === 'o2o-status-updated' ||
            event.eventType === 'modification-pending-approval' ||
            event.eventType === 'modification-expired' ||
            event.eventType === 'modification-approved' ||
            event.eventType === 'modification-rejected',
        ),
        filter((event) => this.isCurrentOrderEvent(event.innerOrderNo)),
        debounceTime(300),
        takeUntil(this.destroy$),
      )
      .subscribe((event) => {
        if (
          event.eventType === 'modification-expired' ||
          event.eventType === 'modification-approved' ||
          event.eventType === 'modification-rejected'
        ) {
          this.showModificationSseToast(event.eventType, event);
        }
        const orderId = this.orderId();
        if (orderId) {
          this.loadOrderDetails(orderId);
        }
      });
  }

  private showModificationSseToast(
    eventType: string,
    event: { outerOrderNo?: string; innerOrderNo?: string; requiredApprovalLevel?: number },
  ): void {
    const orderNo = event.innerOrderNo || event.outerOrderNo || '-';
    const keyMap: Record<string, string> = {
      'modification-expired': 'order_modification.messages.sse_expired',
      'modification-approved': 'order_modification.messages.sse_approved',
      'modification-rejected': 'order_modification.messages.sse_rejected',
    };
    const key = keyMap[eventType];
    if (!key) {
      return;
    }
    this.messageService.add({
      severity: eventType === 'modification-expired' ? 'warn' : 'info',
      summary: this.translateService.instant('common.messages.info'),
      detail: this.translateService.instant(key, {
        orderNo,
        level: event.requiredApprovalLevel,
      }),
    });
  }

  private isCurrentOrderEvent(innerOrderNo?: string): boolean {
    if (!innerOrderNo) {
      return true;
    }
    return innerOrderNo === this.orderData()?.orderInfoVO?.innerOrderNo;
  }

  private loadOrderDetails(orderId: number): void {
    this.loading.set(true);

    this.orderService
      .queryOrderBaseDetail(orderId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: async (response: any) => {
          try {
            let orderData: OrderTotalVO;

            // Handle blob response
            if (response instanceof Blob) {
              const jsonData = await BlobConverterUtil.convertToJson(response);
              orderData = jsonData;
            } else {
              orderData = response;
            }

            // Keep loading true until order items are also loaded
            this.initializeFormConfiguration(orderData);
            this.loadPendingModification(orderId);
          } catch (error) {
            console.error('Error processing order data response:', error);
            this.loading.set(false);
          }
        },
        error: (error) => {
          console.error('Failed to load order details:', error);
          this.loading.set(false);
        },
      });
  }

  private loadPendingModification(orderId: number): void {
    this.modificationApprovalService
      .getPending(orderId)
      .pipe(takeUntil(this.destroy$))
      .subscribe((pending) => {
        this.pendingModification.set(pending);
        this.updateSectionButtons();
        this.headerActions = this.getHeaderActions();
        this.cdr.detectChanges();
      });
  }

  private initializeFormConfiguration(orderData: OrderTotalVO): void {
    const mappedData = this.mapOrderDataToFormData(orderData);
    const innerOrderNo = orderData.orderInfoVO?.innerOrderNo;
    if (innerOrderNo) {
      this.loadStockHoldSummary(innerOrderNo);
    } else {
      this.stockHoldInfo.set(null);
      this.updateStockHoldTrailingSections();
    }

    const orderId = orderData.orderInfoVO?.id;
    if (!orderId) {
      console.error('Order ID not found in order data');
      this.finalizeConfiguration(mappedData, [], orderData);
      return;
    }

    this.loadOrderItems(orderId, mappedData, orderData);
  }

  private loadStockHoldSummary(innerOrderNo: string): void {
    const params: SearchOrderEsParam = {
      page: 0,
      size: 1,
      orderNo: innerOrderNo,
    };
    this.orderService
      .orderPage1(params, 'body', false, {
        httpHeaderAccept: 'application/json',
      } as any)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: any) => {
          const row = response?.pageContent?.[0];
          if (!row) {
            this.stockHoldInfo.set(null);
            this.updateStockHoldTrailingSections();
            return;
          }
          this.stockHoldInfo.set({
            stockHoldCategory: row.stockHoldCategory,
            stockHoldTimeoutMinutes: row.stockHoldTimeoutMinutes,
            stockHoldExpiryTime: row.stockHoldExpiryTime,
            failureReasonDescription: row.failureReasonDescription,
          });
          this.updateStockHoldTrailingSections();
          this.cdr.markForCheck();
        },
        error: () => {
          this.stockHoldInfo.set(null);
          this.updateStockHoldTrailingSections();
        },
      });
  }

  getStockHoldCategoryLabel(category?: string): string {
    if (!category) {
      return this.translateService.instant('stock_hold_timeout.order.no_limit');
    }
    return this.translateService.instant(getStockHoldCategoryLabelKey(category));
  }

  formatStockHoldTimeout(minutes?: number): string {
    return formatTimeoutMinutes(minutes, this.translateService);
  }

  getFailureReasonLabel(reason?: string): string {
    if (isStockHoldTimeoutOrder(reason)) {
      return this.translateService.instant(
        'stock_hold_timeout.order.failure.stock_hold_timeout',
      );
    }
    return reason || '';
  }

  private updateStockHoldTrailingSections(): void {
    const hold = this.stockHoldInfo();
    if (!hold) {
      this.trailingSections = [];
      return;
    }

    const items: SummaryItem[] = [
      {
        label: 'stock_hold_timeout.order.hold_category',
        value: this.getStockHoldCategoryLabel(hold.stockHoldCategory),
        formatter: 'text',
      },
      {
        label: 'stock_hold_timeout.order.timeout',
        value: this.formatStockHoldTimeout(hold.stockHoldTimeoutMinutes),
        formatter: 'text',
      },
      {
        label: 'stock_hold_timeout.order.expiry_time',
        value: isStockHoldTimeoutOrder(hold.failureReasonDescription)
          ? this.translateService.instant('stock_hold_timeout.order.expired')
          : hold.stockHoldExpiryTime
            ? this.formatDateTime(hold.stockHoldExpiryTime)
            : this.translateService.instant('stock_hold_timeout.order.no_limit'),
        formatter: 'text',
      },
    ];

    if (hold.failureReasonDescription) {
      items.push({
        label: 'stock_hold_timeout.order.failure_reason',
        value: this.getFailureReasonLabel(hold.failureReasonDescription),
        formatter: 'text',
      });
    }

    this.trailingSections = [
      {
        title: 'stock_hold_timeout.order.section_title',
        show: true,
        items,
      },
    ];
  }

  /**
   * Load order items from API
   */
  private loadOrderItems(
    orderId: number,
    mappedData: any,
    orderData: OrderTotalVO,
  ): void {
    const itemSearchParams: ItemSearchDTO = {
      orderId: orderId,
      page: 0,
      size: 100,
      sortBy: 'id',
      ascending: true,
    };

    this.orderService
      .queryItemPage(itemSearchParams)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: async (response: any) => {
          try {
            let itemsData: PageInfoOrderItemPageVO;

            // Handle blob response
            if (response instanceof Blob) {
              const jsonData = await BlobConverterUtil.convertToJson(response);
              itemsData = jsonData;
            } else {
              itemsData = response;
            }

            // Map order items to table format
            const orderItems = itemsData.pageContent || [];
            const tableData = orderItems.map((item: any, index: number) => ({
              id: item.id || index,
              spuName: item.spuName || '',
              spuCode: item.spuCode || '',
              skuName: item.skuName || item.spuName || '',
              skuCode: item.skuCode || '',
              lotLabel: Array.isArray(item.lots)
                ? item.lots
                    .map((lot: any) => `${lot.lotValue || lot.batchId} x${lot.qty ?? ''}`)
                    .join(', ')
                : '',
              quantity: item.quantity || 1,
              giftFlag: item.giftFlag === 1,
              setFlag: item.setFlag ?? 0,
              isPackage: item.isPackage ?? Number(item.setFlag) === 1,
              fulfillmentMode: item.fulfillmentMode,
              costPrice: item.thirdSaleAmount / item.quantity || 0,
              costTotal: item.thirdSaleAmount || 0,
              marketplaceListPrice: item.marketplaceListPrice ?? null,
              marketplaceVoucherAmount: item.marketplaceVoucherAmount ?? null,
              appliedUnitPrice:
                item.innerSingleSaleAmount ?? item.salePrice ?? null,
              priceSourceLabel: this.priceSourceLabel(item.priceSource),
              shippingFee: item.transPrice || 0,
              // Sales order create table uses `shipping`
              shipping: item.transPrice || 0,
            }));

            this.enrichPackageChildren(tableData)
              .pipe(takeUntil(this.destroy$))
              .subscribe((enrichedTableData) =>
                this.finalizeConfiguration(
                  mappedData,
                  enrichedTableData,
                  orderData,
                ),
              );
          } catch (error) {
            console.error('Error processing order items response:', error);
            this.finalizeConfiguration(mappedData, [], orderData);
          }
        },
        error: (error) => {
          console.error('Failed to load order items:', error);
          this.finalizeConfiguration(mappedData, [], orderData);
        },
      });
  }

  /**
   * Loads only persisted component lines for Virtual Bundle packages. A
   * Pre-Packed Package is picked as its parent ItemLine, so it must never be
   * expanded from the current Goods BOM.
   */
  private enrichPackageChildren(tableData: any[]) {
    const virtualPackageItems = tableData.filter(
      (item): item is { id: number; fulfillmentMode?: unknown } =>
        (item.isPackage || Number(item.setFlag) === 1) &&
        !isPrePackedPackageFulfillment(item.fulfillmentMode) &&
        Number.isInteger(Number(item.id)),
    );

    if (!virtualPackageItems.length) {
      return of(tableData);
    }

    return forkJoin(
      virtualPackageItems.map((parentItem) =>
        this.orderService
          .queryItemLineList(parentItem.id, 'body', false, {
            httpHeaderAccept: 'application/json' as any,
          })
          .pipe(
            map((lines) => ({ parentId: parentItem.id, lines: lines ?? [] })),
            catchError(() => of({ parentId: parentItem.id, lines: [] })),
          ),
      ),
    )
      .pipe(
        map((groups) => {
          const childrenByParentId = new Map<number, PackageSkuChild[]>();
          groups.forEach(({ parentId, lines }) => {
            childrenByParentId.set(
              parentId,
              lines.map((line) => ({
                spuCode: line.spuCode ?? '',
                spuName: line.spuName ?? '',
                skuCode: line.skuCode ?? '',
                skuName: line.skuName ?? line.spuName ?? '',
                quantityPerPackage: line.quantity ?? 0,
                quantity: line.quantity ?? 0,
              })),
            );
          });
          return tableData.map((item) => {
            const packageChildren = childrenByParentId.get(Number(item.id));
            if (!packageChildren) {
              return item;
            }
            return {
              ...item,
              packageChildren,
            };
          });
        }),
        catchError(() => of(tableData)),
      );
  }

  /**
   * Finalize form configuration with table data
   */
  private finalizeConfiguration(
    mappedData: any,
    tableData: any[],
    orderData: OrderTotalVO,
  ): void {
    // Prefer custom create config (Sales Order); otherwise O2O create config
    const originalConfig = this.createConfigFactory
      ? this.createConfigFactory()
      : getO2OCreateConfig();
    const config = {
      ...originalConfig,
      formFields: originalConfig.formFields.map((f) => ({ ...f })),
      tableSections: originalConfig.tableSections.map((section) => ({
        ...section,
        columns: section.columns?.map((col) => ({ ...col })) || [],
        actions: section.actions?.map((action) => ({ ...action })) || [],
      })),
      summarySection: originalConfig.summarySection
        ? {
            ...originalConfig.summarySection,
            items: originalConfig.summarySection.items.map((item) => ({
              ...item,
            })),
          }
        : originalConfig.summarySection,
      headerActions: (originalConfig.headerActions || []).map((action) => ({
        ...action,
      })),
    };

    // Make all form fields readonly for view mode; ensure no field is hidden so all sections show content
    // Convert dropdown/searchableDropdown to text for fields where we only have display names
    // Convert date fields to text to display formatted dates in view mode
    const deliverySectionKey = 'o2o.create.form.sections.delivery_information';
    const dropdownToTextControls =
      this.formMode === 'standard_sales'
        ? [
            'storeId',
            'channel',
            'payType',
            'provinceId',
            'takingDeliveryWay',
          ]
        : [
            'salesStore',
            'provinceId',
            'communeWardId',
            'paymentMethod',
            'paymentStatus',
            'takingDeliveryWay',
            'shippingUnit',
            'shippingService',
          ];
    const radioToTextControls =
      this.formMode === 'standard_sales'
        ? ['includesShippingFee', 'hasThirdPartyDiscount']
        : [];

    config.formFields = config.formFields.map((field) => {
      const shouldConvertToText =
        (dropdownToTextControls.includes(field.controlName) &&
          (field.type === 'dropdown' || field.type === 'searchableDropdown')) ||
        radioToTextControls.includes(field.controlName);

      const isDateField = field.type === 'date';

      return {
        ...field,
        type: shouldConvertToText || isDateField ? 'text' : field.type,
        readonly: true,
        disabled: true,
        hidden: false, // Always show all fields in view (e.g. Thông tin giao hàng)
        ...(field.section === deliverySectionKey && { dependsOn: undefined }), // Show delivery section regardless of takingDeliveryWay
      };
    });

    // Update table sections with data
    config.tableSections[0].data = tableData;
    config.tableSections[0].selection = [];
    config.tableSections[0].actions = []; // Remove actions in view mode
    config.tableSections[0].hasCheckbox = false; // Remove checkboxes in view mode
    config.tableSections[0].columns = [
      ...(config.tableSections[0].columns ?? []),
      {
        prop: 'appliedUnitPrice',
        label: 'order.detail.applied_unit_price',
        width: 140,
        align: 'right',
        type: 'custom',
        template: 'currencyTemplate',
      },
      {
        prop: 'priceSourceLabel',
        label: 'order.detail.price_source',
        width: 160,
      },
      {
        prop: 'lotLabel',
        label: 'lot_trace.lot',
        width: 180,
      },
    ];
    if (this.showMarketplacePrices) {
      config.tableSections[0].columns = [
        ...(config.tableSections[0].columns ?? []),
        {
          prop: 'marketplaceListPrice',
          label: 'order.marketplace.line.list_price',
          width: 140,
          align: 'right',
          type: 'custom',
          template: 'currencyTemplate',
        },
        {
          prop: 'marketplaceVoucherAmount',
          label: 'order.marketplace.line.voucher_amount',
          width: 150,
          align: 'right',
          type: 'custom',
          template: 'currencyTemplate',
        },
      ];
    }

    // Convert editable columns to display-only format for view mode
    config.tableSections[0].columns = config.tableSections[0].columns?.map(
      (col) => {
        if (col.prop === 'quantity') {
          // Convert quantity to regular display column (not editable)
          return {
            ...col,
            align: 'right',
            type: undefined, // Remove inputNumberHorizontal type
            useGrouping: undefined,
            min: undefined,
            step: undefined,
          };
        }
        // Remove any other editable column types
        if (
          col.type === 'inputNumberHorizontal' ||
          col.type === 'inputNumber'
        ) {
          return {
            ...col,
            type: undefined,
          };
        }
        return col;
      },
    );

    // Update summary section
    const amountInfo = orderData.amountInfoVO;
    const totalDiscount = resolveOrderDetailTotalDiscount(orderData);
    // Use order-level shipping amount instead of summing individual item shipping fees
    const totalShipping = amountInfo?.shippingAmount || 0;

    if (config.summarySection) {
      if (this.summarySectionTitleKey) {
        config.summarySection.title = this.summarySectionTitleKey;
      }
      if (this.formMode === 'standard_sales') {
        // Align with Tạo Đơn hàng bán: items / goods / cost / pay
        config.summarySection.items[0].value = tableData.length;
        config.summarySection.items[1].value =
          amountInfo?.goodsTotalAmount || 0;
        config.summarySection.items[2].value = amountInfo?.costAmount || 0;
        config.summarySection.items[3].value =
          amountInfo?.buyerPayAmount ?? amountInfo?.costAmount ?? 0;
      } else {
        config.summarySection.items[0].value = tableData.length;
        config.summarySection.items[1].value =
          amountInfo?.goodsTotalAmount || 0;
        config.summarySection.items[2].value = totalShipping;
        config.summarySection.items[3].value = amountInfo?.costAmount || 0;
        config.summarySection.items[4].value = totalDiscount;
        config.summarySection.items[5].value =
          amountInfo?.buyerPayAmount || 0;
      }
    }

    // Set configuration first to initialize the form structure
    this.formFields = config.formFields;
    this.tableSections = config.tableSections;
    this.summarySection = config.summarySection;
    this.formConfig.set(config);

    // Trigger change detection to let Angular create the form
    this.cdr.detectChanges();

    this.orderData.set(orderData);

    // Then set the data in the next cycle to ensure form is initialized
    setTimeout(() => {
      this.formData = mappedData;
      this.headerActions = this.getHeaderActions();
      this.updateSectionButtons();
      this.cdr.detectChanges();
      this.loading.set(false);
    }, 0);
  }

  /**
   * Get display label for payment method
   */
  private getPaymentMethodLabel(value: string): string {
    const option = ORDER_INTERNAL_PAYMENT_METHOD_LIST.find(
      (opt) => opt.value === value,
    );
    return option ? this.translateService.instant(option.label) : value;
  }

  /**
   * Get display label for payment status
   */
  private getPaymentStatusLabel(value: number): string {
    const option = ORDER_INTERNAL_PAYMENT_STATUS_LIST.find(
      (opt) => opt.value === value,
    );
    return option
      ? this.translateService.instant(option.label)
      : value.toString();
  }

  /**
   * Get display label for pickup method
   */
  private getPickupMethodLabel(value: string): string {
    const option = ORDER_INTERNAL_PICKUP_METHOD_LIST.find(
      (opt) => opt.value === value,
    );
    return option ? this.translateService.instant(option.label) : value;
  }

  /**
   * Format shipping service for view: "serviceName - totalFee (X giờ)"
   * Hours = expectedDeliveryTime - creationDate. Same style as O2O create form.
   */
  private formatShippingServiceDisplay(
    logisticsInfo?: {
      serviceName?: string;
      totalFee?: number;
      expectedDeliveryTime?: string;
    },
    creationDate?: string,
  ): string {
    if (!logisticsInfo) return '';
    const name = logisticsInfo.serviceName ?? '';
    const fee = logisticsInfo.totalFee;
    const feeStr =
      fee !== undefined && fee !== null ? formatCurrencyVND(Number(fee)) : '';
    let timeStr = '';
    const expectedAt = logisticsInfo.expectedDeliveryTime;
    if (expectedAt && creationDate) {
      const expectedMs = new Date(expectedAt).getTime();
      const createdMs = new Date(creationDate).getTime();
      if (!isNaN(expectedMs) && !isNaN(createdMs)) {
        const hours = Math.round((expectedMs - createdMs) / (1000 * 60 * 60));
        timeStr = ` (${hours} giờ)`;
      }
    }
    if (!name && !feeStr)
      return timeStr ? timeStr.trim().replace(/^\s*\(|\)\s*$/g, '') : '';
    if (!feeStr) return name + timeStr;
    return `${name} - ${feeStr}${timeStr}`;
  }

  private mapOrderDataToFormData(orderData: OrderTotalVO): any {
    if (this.formMode === 'standard_sales') {
      return this.mapStandardSalesOrderDataToFormData(orderData);
    }
    return this.mapO2OOrderDataToFormData(orderData);
  }

  private getYesNoLabel(value: boolean): string {
    return this.translateService.instant(
      value ? 'common.options.yes' : 'common.options.no',
    );
  }

  private priceSourceLabel(source?: string | null): string {
    if (!source) {
      return '';
    }
    const key = `order.detail.price_source_values.${source}`;
    const translated = this.translateService.instant(key);
    return translated !== key ? translated : source;
  }

  private getChannelDisplayLabel(channel?: string, channelMeaning?: string): string {
    if (channelMeaning) {
      return channelMeaning;
    }
    if (!channel) {
      return '';
    }
    const key = `order.internal.channels.${String(channel).toLowerCase()}`;
    const translated = this.translateService.instant(key);
    return translated !== key ? translated : channel;
  }

  /** Map API order → Tạo Đơn hàng bán field names. */
  private mapStandardSalesOrderDataToFormData(orderData: OrderTotalVO): any {
    const orderInfo = orderData.orderInfoVO;
    const receiverInfo = orderData.receiverInfoVO;
    const amountInfo = orderData.amountInfoVO;
    const paymentInfo = orderData.paymentInfoVO;
    const logisticsInfo = orderData.logisticsInfoVO;

    const provinceName = receiverInfo?.province || '';
    const provinceOption = this.provinceOptions().find(
      (p) => p.label === provinceName,
    );
    const provinceId = provinceOption ? provinceOption.value : null;
    this.originalProvinceId = provinceId;

    const totalDiscount = resolveOrderDetailTotalDiscount(orderData);
    const shippingAmount = Number(amountInfo?.shippingAmount) || 0;

    return {
      outerOrderNo: orderInfo?.outerOrderNo || '',
      orderTime: orderInfo?.orderTime
        ? this.formatDateOnly(orderInfo.orderTime)
        : '',
      channel: this.getChannelDisplayLabel(
        orderInfo?.channel,
        orderInfo?.channelTypeMeaning,
      ),
      orderRemarks: orderInfo?.orderRemarks || '',
      takingDeliveryWay: orderInfo?.takingDeliveryWay
        ? this.getPickupMethodLabel(orderInfo.takingDeliveryWay)
        : '',
      storeId: orderInfo?.storeName || '',
      buyerNick: receiverInfo?.buyerNick || '',
      receiverName: receiverInfo?.receiverName || '',
      mobile: receiverInfo?.mobile || receiverInfo?.phone || '',
      provinceId: provinceName,
      address: receiverInfo?.address || '',
      buyerRemarks: orderInfo?.buyerRemarks || '',
      payType: paymentInfo?.payType
        ? this.getPaymentMethodLabel(paymentInfo.payType)
        : '',
      payTime: paymentInfo?.payTime
        ? this.formatDateTime(paymentInfo.payTime)
        : '',
      goodsTotalAmount: Number(amountInfo?.goodsTotalAmount) || 0,
      costTotalAmount: Number(amountInfo?.costAmount) || 0,
      payAmount:
        Number(paymentInfo?.payAmount) ||
        Number(amountInfo?.buyerPayAmount) ||
        0,
      includesShippingFee: this.getYesNoLabel(shippingAmount > 0),
      hasThirdPartyDiscount: this.getYesNoLabel(totalDiscount > 0),
      trackingNumber: logisticsInfo?.logisticsNo ?? '',
    };
  }

  private mapO2OOrderDataToFormData(orderData: OrderTotalVO): any {
    const orderInfo = orderData.orderInfoVO;
    const receiverInfo = orderData.receiverInfoVO;
    const amountInfo = orderData.amountInfoVO;
    const paymentInfo = orderData.paymentInfoVO;
    const logisticsInfo = orderData.logisticsInfoVO;

    const provinceName = receiverInfo?.province || '';
    const provinceOption = this.provinceOptions().find(
      (p) => p.label === provinceName,
    );
    const wardName = receiverInfo?.ward || '';
    const provinceId = provinceOption ? provinceOption.value : null;

// Store original IDs for saving (province and ward are used for store filtering)
    this.originalProvinceId = provinceId;
    const businessType = (orderInfo?.businessType ?? '').toString().trim().toUpperCase();
    const channel = (orderInfo?.channel ?? '').toString().trim().toUpperCase();
    const isLazadaChannel = isLazadaO2oOrder(businessType, channel);
    const channelMeaning = (orderInfo?.channelTypeMeaning ?? '').toString().trim();

    const mappedData = {
      // Order Information - matching O2O create form field names
      thirdPartyOrderNumber: orderInfo?.outerOrderNo || '',
      // Format date only (without time) for view mode: dd/MM/yyyy
      thirdPartyOrderTime: orderInfo?.orderTime
        ? this.formatDateOnly(orderInfo.orderTime)
        : '',
      // Sales channel: use value 'LAZADA' to match dropdown option when order is from Lazada (E-COMMERCE + LAZADA)
      salesChannel: isLazadaChannel
        ? 'LAZADA'
        : channelMeaning || orderInfo?.channel || 'website',
      salesStore: orderInfo?.storeName || '',
// Delivery method: use "Delivery" for marketplace (Lazada) orders when applicable
      takingDeliveryWay: orderInfo?.takingDeliveryWay
        ? isLazadaChannel
          ? this.translateService.instant('constant.status.delivery')
          : this.getPickupMethodLabel(orderInfo.takingDeliveryWay)
        : isLazadaChannel
          ? this.translateService.instant('constant.status.delivery')
          : '',

      // Buyer & Recipient Information
      buyerNickname: receiverInfo?.buyerNick || '',
      recipient: receiverInfo?.receiverName || '',
      recipientPhone: receiverInfo?.mobile || receiverInfo?.phone || '',
      shippingAddress: receiverInfo?.address || '',
      // Show province name as text in view mode (since field is converted to text type)
      provinceId: provinceName, // Use province name for display in view mode
      province: provinceName,
      // Show commune/ward name from API response
      communeWardId: wardName,
      communeWard: wardName,
      buyerRemarks: orderInfo?.buyerRemarks || '',

      // Delivery Information (from logisticsInfoVO)
      shippingUnit: logisticsInfo?.providerName ?? '',
      shippingService: this.formatShippingServiceDisplay(
        logisticsInfo,
        orderInfo?.creationDate,
      ),
      packageLength: logisticsInfo?.length ?? 0,
      packageWidth: logisticsInfo?.width ?? 0,
      packageHeight: logisticsInfo?.height ?? 0,
      packageWeight: logisticsInfo?.weight ?? 0,
      cod: logisticsInfo?.codAmount ?? 0,
      declaredValue: logisticsInfo?.declaredValue ?? 0,
      deliveryNote: logisticsInfo?.deliveryNote ?? '',
      trackingNumber: logisticsInfo?.logisticsNo ?? '',

      // Payment Information
      // Show payment method label instead of value
      paymentMethod: paymentInfo?.payType
        ? this.getPaymentMethodLabel(paymentInfo.payType)
        : '',
      // Show payment status label instead of value
      paymentStatus:
        paymentInfo?.payStatus !== undefined
          ? this.getPaymentStatusLabel(Number(paymentInfo.payStatus))
          : '',
      // Format date as readable string for text field in view mode
      paymentTime: paymentInfo?.payTime
        ? this.formatDateTime(paymentInfo.payTime)
        : '',
      paymentAmount: Number(paymentInfo?.payAmount) || 0,
      goodsTotalAmount: Number(amountInfo?.goodsTotalAmount) || 0,
      costTotalAmount: Number(amountInfo?.costAmount) || 0,
      shippingAmount: Number(amountInfo?.shippingAmount) || 0,

      // Additional fields for reference
      merchantRemarks: orderInfo?.orderRemarks || '',
      serviceRemarks: orderInfo?.serviceRemarks || '',
      tags: orderInfo?.tags || '',
      takingDeliveryStoreName: orderInfo?.takingDeliveryStoreName || '',
      salesEmployeeRealName: orderInfo?.salesEmployeeRealName || '',
      buyerPayAmount: amountInfo?.buyerPayAmount || 0,
    };

    return mappedData;
  }

  onFormAction(event: { action: string; data?: any }): void {
    if (event.action === 'cancel' || event.action === 'back') {
      this.router.navigate([this.backRoute]);
    } else if (event.action === 'approveModification') {
      this.onApprovePendingModification();
    } else if (event.action === 'rejectModification') {
      this.onRejectPendingModification();
    } else if (event.action === 'formValueChange' && event.data) {
      // Update formData when fields change
      this.formData = { ...this.formData, ...event.data };
      // Also call onFormValueChanges for any additional processing
      this.onFormValueChanges(event.data);
    }
  }

  onTableAction(event: { section: string; action: string; data?: any }): void {
    // Handle table actions if needed
  }

  onSectionAction(event: {
    section: string;
    action: string;
    data?: any;
  }): void {
    if (event.action === 'editReceiverInfo') {
      this.onEditReceiverInfo();
    }
    if (event.action === 'saveEdit') {
      this.onSaveReceiverInfo();
    }
    if (event.action === 'cancelEdit') {
      this.onCancelEditReceiverInfo();
    }
    if (event.action === 'approveModification') {
      this.onApprovePendingModification();
    }
    if (event.action === 'rejectModification') {
      this.onRejectPendingModification();
    }
  }

  onFormValueChanges(value: any): void {
    // Update form data when user makes changes in edit mode
    if (this.isEditingReceiverInfo()) {
      this.formData = { ...this.formData, ...value };
    }
  }

  getHeaderActions(): ActionButton[] {
    if (this.embedMode) {
      return [];
    }
    const actions: ActionButton[] = [
      {
        label: 'common.actions.go_back',
        action: 'back',
        outlined: true,
        severity: 'secondary',
      },
    ];
    if (this.canApprovePendingModification()) {
      actions.unshift(
        {
          label: 'common.actions.approve',
          action: 'approveModification',
          severity: 'success',
        },
        {
          label: 'common.actions.reject',
          action: 'rejectModification',
          severity: 'danger',
          outlined: true,
        },
      );
    }
    return actions;
  }

  updateSectionButtons(): void {
    // If edit button is disabled, always return empty array
    if (!this.enableEditButton) {
      this.sectionButtons = [];
      return;
    }

    if (this.pendingModification()) {
      // Block inline edit while a modification is awaiting approval
      this.sectionButtons = [];
      this.cdr.detectChanges();
      return;
    }

    if (this.isEditingReceiverInfo()) {
      // Show save and cancel buttons when editing
      const buttons: SectionButton[] = [
        {
          label: 'common.actions.save',
          action: 'saveEdit',
          section: 'o2o.create.form.sections.buyer_recipient_information',
          icon: 'Save',
          severity: 'secondary',
          outlined: true,
        },
        {
          label: 'common.actions.cancel',
          action: 'cancelEdit',
          section: 'o2o.create.form.sections.buyer_recipient_information',
          icon: 'Close',
          severity: 'secondary',
          outlined: true,
        },
      ];
      this.sectionButtons = this.canUpdate() ? buttons : [];
    } else {
      // Show edit button when not editing (only if user has update permission)
      this.sectionButtons = this.canUpdate()
        ? [
            {
              label: 'common.actions.edit',
              action: 'editReceiverInfo',
              section: 'o2o.create.form.sections.buyer_recipient_information',
              icon: 'Edit',
              severity: 'secondary',
              outlined: true,
            },
          ]
        : [];
    }
    this.cdr.detectChanges();
  }

  // Inline edit methods
  onEditReceiverInfo(): void {
    if (!this.canUpdate()) {
      // Show warning message if needed
      this.messageService.add({
        severity: 'warn',
        summary: this.translateService.instant('common.messages.warning'),
        detail: this.translateService.instant(
          'common.permissions.no_permission',
        ),
      });
      return;
    }

    // Store original data for cancel functionality
    // Make sure we store the data with province name for proper restoration
    const currentData = this.formData;
    this.originalReceiverData = { ...currentData };
    this.isEditingReceiverInfo.set(true);

    // Update form fields to be editable for receiver info
    this.updateFormFieldsForEdit();
    this.updateSectionButtons();
  }

  onCancelEditReceiverInfo(): void {
    // Restore original data
    this.formData = { ...this.originalReceiverData };
    this.isEditingReceiverInfo.set(false);

    // Update form fields back to readonly
    this.updateFormFieldsForView();
    this.updateSectionButtons();
  }

  async onSaveReceiverInfo(): Promise<void> {
    if (!this.canUpdate()) {
      this.messageService.add({
        severity: 'warn',
        summary: this.translateService.instant('common.messages.warning'),
        detail: this.translateService.instant(
          'common.permissions.no_permission',
        ),
      });
      return;
    }

    if (!this.orderId()) {
      this.messageService.add({
        severity: 'error',
        summary: this.translateService.instant('common.messages.error'),
        detail: this.translateService.instant(
          'o2o.edit.form.validation.order_id_required',
        ),
      });
      return;
    }

    try {
      const currentFormData = this.formData;

      // Debug log to verify buyerRemarks is in formData
      console.log('=== Saving Receiver Info ===');
      console.log('formData:', currentFormData);
      console.log('buyerRemarks:', currentFormData.buyerRemarks);

      // Transform form data to OrderBaseDTO format
      const orderBaseDTO: OrderBaseDTO = {
        orderId: this.orderId()!,
        orderRemarks: currentFormData.merchantRemarks || '',
        buyerRemarks: currentFormData.buyerRemarks || '',
        serviceRemarks: currentFormData.serviceRemarks || '',
        buyerNick: currentFormData.buyerNickname || '',
        tags: currentFormData.tags || '',
        payAmount:
          currentFormData.paymentAmount === undefined ||
          currentFormData.paymentAmount === null ||
          currentFormData.paymentAmount === ''
            ? undefined
            : Number(currentFormData.paymentAmount),
        userName: this.authService.currentUser()?.username,
        fullName:
          [
            this.authService.currentUser()?.firstName,
            this.authService.currentUser()?.lastName,
          ]
            .filter(Boolean)
            .join(' ') || this.authService.currentUser()?.username,
        receiverInfoDTO: {
          receiverName: currentFormData.recipient || '',
          phone: '',
          mobile: currentFormData.recipientPhone || '',
          // Use stored original province ID (provinceId in formData contains the display name)
          provinceCode: this.originalProvinceId || currentFormData.provinceId || '',
          province: currentFormData.provinceId || '', // This contains the province name
          cityCode: currentFormData.city || '',
          city: currentFormData.city || '',
          districtCode: currentFormData.district || '',
          district: currentFormData.district || '',
          // Use communeWardId from formData (contains ward name)
          town: currentFormData.communeWardId || '',
          address: currentFormData.shippingAddress || '',
        },
      };

      const result = await this.modificationApprovalService
        .submitOrderBaseEdit(orderBaseDTO)
        .toPromise();

      if (result?.outcome === 'PENDING_APPROVAL') {
        this.messageService.add({
          severity: 'info',
          summary: this.translateService.instant('common.messages.success'),
          detail: this.translateService.instant(
            'order_modification.messages.pending_approval',
            {
              level: result.requiredApprovalLevel,
            },
          ),
        });
        this.isEditingReceiverInfo.set(false);
        this.updateFormFieldsForView();
        if (this.orderId()) {
          this.loadPendingModification(this.orderId()!);
          this.loadOrderDetails(this.orderId()!);
        }
        return;
      }

      if (result) {
        // Show success toast
        this.messageService.add({
          severity: 'success',
          summary: this.translateService.instant('common.messages.success'),
          detail: this.translateService.instant(
            'o2o.edit.form.messages.order_updated_successfully',
          ),
        });

        // Update original data with new values
        this.originalReceiverData = { ...this.formData };
        this.isEditingReceiverInfo.set(false);

        // Update form fields back to readonly
        this.updateFormFieldsForView();
        this.updateSectionButtons();
      }
    } catch (err: any) {
      console.error('Error updating receiver info:', err);

      // Show error toast
      this.messageService.add({
        severity: 'error',
        summary: this.translateService.instant('common.messages.error'),
        detail:
          err?.error?.message ||
          err?.message ||
          this.translateService.instant(
            'o2o.edit.form.messages.failed_to_update_order',
          ),
      });
    }
  }

  onApprovePendingModification(): void {
    const pending = this.pendingModification();
    const level = this.maxModificationApproveLevel();
    if (pending?.id == null || !this.canApprovePendingModification()) {
      return;
    }
    this.modificationApprovalService
      .approve(pending.id, level)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.messageService.add({
            severity: 'success',
            summary: this.translateService.instant('common.messages.success'),
            detail: this.translateService.instant(
              'order_modification.messages.approved',
            ),
          });
          this.pendingModification.set(null);
          if (this.orderId()) {
            this.loadOrderDetails(this.orderId()!);
          }
        },
        error: (err) => {
          this.messageService.add({
            severity: 'error',
            summary: this.translateService.instant('common.messages.error'),
            detail:
              err?.error?.message ||
              this.translateService.instant(
                'order_modification.messages.approve_failed',
              ),
          });
        },
      });
  }

  onRejectPendingModification(): void {
    const pending = this.pendingModification();
    if (pending?.id == null || !this.canApprovePendingModification()) {
      return;
    }
    this.modificationApprovalService
      .reject(pending.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.messageService.add({
            severity: 'success',
            summary: this.translateService.instant('common.messages.success'),
            detail: this.translateService.instant(
              'order_modification.messages.rejected',
            ),
          });
          this.pendingModification.set(null);
          this.updateSectionButtons();
          this.headerActions = this.getHeaderActions();
          this.cdr.detectChanges();
        },
        error: (err) => {
          this.messageService.add({
            severity: 'error',
            summary: this.translateService.instant('common.messages.error'),
            detail:
              err?.error?.message ||
              this.translateService.instant(
                'order_modification.messages.reject_failed',
              ),
          });
        },
      });
  }

  private updateFormFieldsForEdit(): void {
    // L1: notes / recipient / phone / address
    // L2/L3: payment amount changes (threshold evaluated server-side)
    const editableFields = [
      'buyerNickname',
      'buyerRemarks',
      'merchantRemarks',
      'serviceRemarks',
      'recipient',
      'recipientPhone',
      'shippingAddress',
      'paymentAmount',
    ];

    // Make only the specified fields editable
    this.formFields = this.formFields.map((field) => {
      const isEditableField = editableFields.includes(field.controlName);

      // Province and ward should remain disabled and readonly (keep old data)
      const isProvinceOrWard =
        field.controlName === 'provinceId' ||
        field.controlName === 'communeWardId';

      if (isEditableField) {
        // Enable editable fields
        return {
          ...field,
          readonly: false,
          disabled: false,
        };
      } else if (isProvinceOrWard) {
        // Keep province and ward disabled but visible (keep old data)
        return {
          ...field,
          readonly: true,
          disabled: true,
          // Keep as text type to preserve the display value
          type: 'text',
        };
      } else {
        // Keep all other fields disabled and readonly
        return {
          ...field,
          readonly: true,
          disabled: true,
        };
      }
    });

    // No need to convert province/ward - keep them as display text
    // The old data is preserved in formData
    this.cdr.detectChanges();
  }

  private updateFormFieldsForView(): void {
    // Get current form data first
    const currentFormData = this.formData;

    // Convert province ID back to province name for view mode
    const provinceId = currentFormData.provinceId;
    const provinceName = this.getProvinceName(provinceId);

    // Make all fields readonly again
    this.formFields = this.formFields.map((field) => {
      const shouldConvertToText =
        (field.controlName === 'salesStore' ||
          field.controlName === 'provinceId' ||
          field.controlName === 'communeWardId' ||
          field.controlName === 'paymentMethod' ||
          field.controlName === 'paymentStatus' ||
          field.controlName === 'takingDeliveryWay') &&
        (field.type === 'dropdown' || field.type === 'searchableDropdown');

      const isDateField = field.type === 'date';

      return {
        ...field,
        type: shouldConvertToText || isDateField ? 'text' : field.type,
        readonly: true,
        disabled: true,
      };
    });

    // Update form data with province name for display
    this.formData = {
      ...currentFormData,
      provinceId: provinceName || provinceId, // Fallback to ID if name not found
    };
    this.cdr.detectChanges();
  }
}
